<template>
  <BbPopup
    v-model="referencesListOpened.open"
    :showCloseIcon="true"
    contentClass="references-list-popup layouts-list-popup"
  >
    <template #popup-header>
      <div>
        <TextBody tag="span" i18n-key="author.manageReferences" :body-type="2" weight="medium" />
        <div class="element-label__show-footnotes-wrapper" v-if="referencesListOpened.editable">
          <div>{{ t('author.displayReferences') }}</div>
          <BbSwitch v-model="footnotesPositionValue" :items="footnotesPositionValues" class="element-label__switch" />
        </div>
      </div>
    </template>
    <ReferencesList :is-editable="referencesListOpened.editable" @update-references="refreshReferences" />
  </BbPopup>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { ref, watch } from 'vue';
import { useWesley } from '@anthill-modules/wesley-components';

import ReferencesList from '~/components/common/references/ReferencesList.vue';
import TextBody from '~/components/common/TextBody.vue';
import useModals from '~/composables/useModals';
import useDocumentEditor from '~/composables/useDocumentEditor';
import useAuthoring from '~/composables/useAuthoring';
import {
  displayAllFreshReferencesAtTheEnd,
  removeOrHideDocumentReferences,
  updateDocumentLayoutsShowFootnotesToFalse,
} from '~/utils/footnotes';

export default {
  components: {
    TextBody,
    ReferencesList,
  },
  setup() {
    const { t } = useI18n();

    const { referencesListOpened } = useModals();
    const {
      sendMessage: sendMessageToDocument,
      asyncSendMessage: sendAsyncMessageToDocument,
      iframeWindow,
    } = useWesley();
    const { currentDesignSystemDocumentElements, updateEditorSelectableElementsData, refreshReferencesOnUpdate } =
      useDocumentEditor();
    const { setDisplayReferencesOnDocumentLevel, footnotes, displayReferencesOnDocumentLevel } = useAuthoring();

    const footnotesPositionValues = [
      {
        title: t('author.showReferencesBlockLevel'),
        value: 0,
      },
      {
        title: t('author.showReferencesDocumentLevel'),
        value: 1,
      },
    ];

    const iframeBody = ref(null);
    const footnotesPositionValue = ref(displayReferencesOnDocumentLevel.value ? 1 : 0);

    watch(
      () => [iframeWindow.value, referencesListOpened.value],
      async ([newIframeWindow, opened]) => {
        if (opened.open) {
          iframeBody.value = newIframeWindow?.document?.body || null;
          const displayOnDocumentLevel =
            iframeBody.value && iframeBody.value.attributes?.['show-footnotes-on-document']?.value === 'true' ? 1 : 0;
          footnotesPositionValue.value = displayOnDocumentLevel;
          setDisplayReferencesOnDocumentLevel(displayOnDocumentLevel === 1);
        }
      },
      { immediate: true, deep: true },
    );

    watch(footnotesPositionValue, () => {
      if (iframeBody?.value?.attributes?.id?.value) {
        sendMessageToDocument({
          method: 'updateAttribute',
          params: {
            query: `#${iframeBody.value.attributes.id.value}`,
            attr: 'show-footnotes-on-document',
            value: footnotesPositionValue.value === 1,
          },
        });
        iframeBody.value.setAttribute(
          'show-footnotes-on-document',
          footnotesPositionValue.value === 1 ? 'true' : 'false',
        );
        setDisplayReferencesOnDocumentLevel(footnotesPositionValue.value === 1);

        // if footnotes are set to be shown on document level, hide them from layouts, and display all footnotes at the end
        if (footnotesPositionValue.value === 1) {
          updateDocumentLayoutsShowFootnotesToFalse({
            currentDesignSystemDocumentElements: currentDesignSystemDocumentElements.value,
            sendMessageToDocument,
          });
          // we have to wait until references are removed from dom
          setTimeout(() => {
            displayAllFreshReferencesAtTheEnd({
              htmlString: iframeBody.value?.innerHTML,
              currentDesignSystemDocumentElements: currentDesignSystemDocumentElements.value,
              footnotes: footnotes.value,
              sendMessageToDocument,
              sendAsyncMessageToDocument,
              t,
            });
          }, 500);
        } else {
          removeOrHideDocumentReferences({
            currentDesignSystemDocumentElements: currentDesignSystemDocumentElements.value,
            sendMessageToDocument,
          });
        }
        updateEditorSelectableElementsData();
      }
    });

    const refreshReferences = () => {
      if (iframeBody.value?.innerHTML) {
        setTimeout(() => {
          refreshReferencesOnUpdate();
        }, 2000);
      }
    };

    return {
      footnotesPositionValue,
      footnotesPositionValues,
      referencesListOpened,
      refreshReferences,
      t,
    };
  },
};
</script>

<style scoped lang="scss">
.element-label {
  &__show-footnotes-wrapper {
    padding: $bb-spacing-1 $bb-spacing-2;
    border-radius: $bb-spacing-1;
    border: 1px solid $bb-neutral-4;
    display: flex;
    gap: $bb-spacing-2;
    align-items: center;
    font-weight: 500;
    z-index: 2;
    font-size: 12px;
    color: $bb-neutral-1;
    margin-top: $bb-spacing-3;
    height: $bb-spacing-12;
  }

  &__switch {
    pointer-events: inherit;
    width: 420px !important;
    background: transparent;

    :deep(.switch-item) {
      height: $bb-spacing-4 !important;
      padding: 0 !important;
    }

    :deep(.switch-item .v-slide-group__container .v-slide-group__content) {
      button {
        height: $bb-spacing-8;
      }
    }

    :deep(.switch-item .v-slide-group__container .v-slide-group__content .v-tab--selected) {
      background-color: $bb-primary-3 !important;
    }
  }
}
</style>
